import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Bomb, Gem, DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Mines() {
  const { user, token, refreshUser } = useAuth();
  const [betAmount, setBetAmount] = useState(10);
  const [minesCount, setMinesCount] = useState(5);
  const [gameId, setGameId] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [revealedTiles, setRevealedTiles] = useState([]);
  const [minePositions, setMinePositions] = useState([]);
  const [currentMultiplier, setCurrentMultiplier] = useState(1.0);
  const [nextMultiplier, setNextMultiplier] = useState(1.0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'RBX.Casino | Mines';
  }, []);

  const startGame = async () => {
    if (!token) return toast.error('Please login first');
    if (betAmount < 1) return toast.error('Minimum bet is R$1');
    if (betAmount > (user?.site_balance || 0)) return toast.error('Insufficient balance');
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/games/mines/create`,
        { bet_amount: betAmount, mines_count: minesCount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGameId(response.data.game_id);
      setGameState('active');
      setIsPlaying(true);
      setRevealedTiles([]);
      setMinePositions([]);
      setCurrentMultiplier(1.0);
      setNextMultiplier(response.data.next_multiplier);
      refreshUser();
      toast.success('Game started!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start game');
    } finally {
      setLoading(false);
    }
  };

  const revealTile = async (index) => {
    if (!isPlaying || revealedTiles.includes(index) || loading) return;
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/games/mines/reveal`,
        { game_id: gameId, tile_index: index },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRevealedTiles([...revealedTiles, index]);
      
      if (response.data.is_mine) {
        setMinePositions(response.data.mine_positions);
        setGameState('lost');
        setIsPlaying(false);
        toast.error('BOOM! You hit a mine!');
        refreshUser();
      } else if (response.data.game_over) {
        setMinePositions(response.data.mine_positions);
        setGameState('won');
        setIsPlaying(false);
        setCurrentMultiplier(response.data.multiplier);
        toast.success(`All safe tiles revealed! Won R$${response.data.payout.toFixed(2)}`);
        refreshUser();
      } else {
        setCurrentMultiplier(response.data.multiplier);
        setNextMultiplier(response.data.next_multiplier);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reveal tile');
    } finally {
      setLoading(false);
    }
  };

  const cashout = async () => {
    if (!isPlaying || revealedTiles.length === 0) return;
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/games/mines/cashout?game_id=${gameId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMinePositions(response.data.mine_positions);
      setGameState('won');
      setIsPlaying(false);
      toast.success(`Cashed out R$${response.data.payout.toFixed(2)} at ${response.data.multiplier}x!`);
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cash out');
    } finally {
      setLoading(false);
    }
  };

  const getTileContent = (index) => {
    if (minePositions.includes(index)) {
      return <Bomb className="w-6 h-6 text-red-500" />;
    }
    if (revealedTiles.includes(index)) {
      return <Gem className="w-6 h-6 text-green-400" />;
    }
    return null;
  };

  const getTileStyle = (index) => {
    if (minePositions.includes(index)) {
      return 'bg-red-500/20 border-red-500';
    }
    if (revealedTiles.includes(index)) {
      return 'bg-green-500/20 border-green-500';
    }
    return 'bg-slate-800/50 border-slate-700 hover:border-blue-500 cursor-pointer';
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
        <Bomb className="text-red-500" /> Mines
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Game Grid */}
        <div className="lg:col-span-2">
          <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800">
            <div className="grid grid-cols-5 gap-2">
              {Array.from({ length: 25 }).map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => revealTile(index)}
                  disabled={!isPlaying || revealedTiles.includes(index) || minePositions.length > 0}
                  className={`aspect-square rounded-xl border-2 flex items-center justify-center transition-all ${getTileStyle(index)}`}
                  whileHover={isPlaying && !revealedTiles.includes(index) ? { scale: 1.05 } : {}}
                  whileTap={isPlaying && !revealedTiles.includes(index) ? { scale: 0.95 } : {}}
                >
                  <AnimatePresence mode="wait">
                    {getTileContent(index) ? (
                      <motion.div
                        key="content"
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        exit={{ scale: 0 }}
                      >
                        {getTileContent(index)}
                      </motion.div>
                    ) : (
                      <div className="font-display font-bold leading-none text-center select-none">
                        <div className="text-[11px] text-white">RBX</div>
                        <div className="text-[9px] text-red-500">CASINO</div>
                      </div>
                    )}
                  </AnimatePresence>
                </motion.button>
              ))}
            </div>

            {/* Multiplier Display */}
            {isPlaying && (
              <div className="mt-4 text-center">
                <p className="text-slate-400">Current: <span className="text-green-400 font-bold text-xl">{currentMultiplier}x</span></p>
                <p className="text-slate-500 text-sm">Next tile: {nextMultiplier}x</p>
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="space-y-4">
          <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800">
            {!isPlaying ? (
              <>
                <div className="mb-4">
                  <label className="text-slate-400 text-sm mb-2 block">Bet Amount</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                    <input
                      type="number"
                      value={betAmount}
                      onChange={(e) => setBetAmount(Number(e.target.value))}
                      className="w-full bg-slate-800/50 border border-slate-700 rounded-xl pl-10 pr-4 py-3 text-white"
                      min={1}
                    />
                  </div>
                  <div className="flex gap-2 mt-2">
                    {[10, 50, 100, 500].map((amt) => (
                      <button
                        key={amt}
                        onClick={() => setBetAmount(amt)}
                        className="flex-1 py-1 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm text-slate-300"
                      >
                        {amt}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="text-slate-400 text-sm mb-2 block">Mines: {minesCount}</label>
                  <input
                    type="range"
                    min={1}
                    max={24}
                    value={minesCount}
                    onChange={(e) => setMinesCount(Number(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>1 (Safe)</span>
                    <span>24 (Risky)</span>
                  </div>
                </div>

                <button
                  onClick={startGame}
                  disabled={loading || !user}
                  className="w-full py-3 bg-green-600 hover:bg-green-500 disabled:bg-slate-700 rounded-xl text-white font-bold transition-colors"
                >
                  {loading ? 'Starting...' : 'Start Game'}
                </button>
              </>
            ) : (
              <>
                <div className="text-center mb-4">
                  <p className="text-slate-400">Bet: <span className="text-white font-bold">R${betAmount}</span></p>
                  <p className="text-slate-400">Mines: <span className="text-red-400 font-bold">{minesCount}</span></p>
                  <p className="text-slate-400">Revealed: <span className="text-green-400 font-bold">{revealedTiles.length}</span></p>
                </div>

                <div className="bg-slate-800/50 rounded-xl p-4 mb-4 text-center">
                  <p className="text-slate-400 text-sm">Potential Win</p>
                  <p className="text-2xl font-bold text-green-400">R${(betAmount * currentMultiplier).toFixed(2)}</p>
                </div>

                <button
                  onClick={cashout}
                  disabled={loading || revealedTiles.length === 0}
                  className="w-full py-3 bg-yellow-600 hover:bg-yellow-500 disabled:bg-slate-700 rounded-xl text-white font-bold transition-colors"
                >
                  {loading ? 'Processing...' : `Cash Out ${currentMultiplier}x`}
                </button>
              </>
            )}
          </div>

          {gameState === 'lost' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 text-center"
            >
              <p className="text-red-400 font-bold">Game Over!</p>
              <p className="text-slate-400 text-sm">You hit a mine</p>
              <button
                onClick={() => { setGameState(null); setRevealedTiles([]); setMinePositions([]); }}
                className="mt-2 px-4 py-2 bg-slate-800 rounded-lg text-white text-sm"
              >
                Play Again
              </button>
            </motion.div>
          )}

          {gameState === 'won' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-green-500/20 border border-green-500/50 rounded-xl p-4 text-center"
            >
              <p className="text-green-400 font-bold">You Won!</p>
              <p className="text-white text-lg">R${(betAmount * currentMultiplier).toFixed(2)}</p>
              <button
                onClick={() => { setGameState(null); setRevealedTiles([]); setMinePositions([]); }}
                className="mt-2 px-4 py-2 bg-slate-800 rounded-lg text-white text-sm"
              >
                Play Again
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
